import React, { useState } from 'react';
import { API_BASE } from '@/config';
import { Download, RefreshCw, Lock } from 'lucide-react';

const Admin = () => {
  const [key, setKey] = useState(sessionStorage.getItem('ppv_admin_key') || '');
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async (k = key) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/leads`, { headers: { 'x-admin-key': k } });
      if (!res.ok) throw new Error('unauthorized');
      const data = await res.json();
      setLeads(data.leads);
      sessionStorage.setItem('ppv_admin_key', k);
    } catch {
      setError('Invalid admin key or server error.');
      setLeads(null);
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    const res = await fetch(`${API_BASE}/leads/export`, { headers: { 'x-admin-key': key } });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'palm-vista-leads.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!leads) {
    return (
      <section className="pt-32 pb-24 bg-ivory min-h-[80vh]">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white border border-line rounded-3xl p-8 shadow-lg" data-testid="admin-login">
            <span className="h-11 w-11 rounded-full bg-palm flex items-center justify-center"><Lock className="h-5 w-5 text-gold" /></span>
            <h1 className="font-display text-3xl font-medium text-palm mt-4">Leads Admin</h1>
            <p className="text-sm text-muted-foreground mt-2">Enter the admin key to view enquiries.</p>
            <input type="password" value={key} onChange={(e) => setKey(e.target.value)} data-testid="admin-key-input"
              placeholder="Admin key" className="w-full mt-5 rounded-xl border border-line px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
            {error && <p className="text-xs text-red-500 mt-2" data-testid="admin-error">{error}</p>}
            <button onClick={() => load()} disabled={loading} data-testid="admin-login-btn"
              className="w-full mt-4 rounded-full bg-palm text-ivory py-3 text-sm font-bold hover:bg-palm-2 transition-colors disabled:opacity-60">
              {loading ? 'Loading…' : 'View Leads'}
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-28 pb-24 bg-ivory min-h-[80vh]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-medium text-palm">Leads <span className="text-gold">({leads.length})</span></h1>
            <p className="text-sm text-muted-foreground mt-1">All enquiry form submissions, newest first.</p>
          </div>
          <div className="flex gap-3">
            <button onClick={() => load()} data-testid="admin-refresh-btn" className="inline-flex items-center gap-2 rounded-full border border-palm/25 text-palm px-5 py-2.5 text-sm font-semibold hover:border-gold transition-all">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button onClick={exportCsv} data-testid="admin-export-btn" className="inline-flex items-center gap-2 rounded-full bg-palm text-ivory px-5 py-2.5 text-sm font-semibold hover:bg-palm-2 transition-colors">
              <Download className="h-4 w-4 text-gold" /> Export CSV
            </button>
          </div>
        </div>
        <div className="mt-8 overflow-x-auto rounded-2xl border border-line bg-white shadow-sm" data-testid="admin-leads-table">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="bg-palm text-ivory text-left">
                {['Date', 'Name', 'Mobile', 'Email', 'Config', 'Source', 'UTM Source', 'UTM Campaign', 'Page'].map((h) => (
                  <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">No leads yet.</td></tr>
              )}
              {leads.map((l, i) => (
                <tr key={i} className="border-t border-line hover:bg-paper/50">
                  <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{l.created_at ? new Date(l.created_at).toLocaleString('en-IN') : '—'}</td>
                  <td className="px-4 py-3 font-semibold text-palm">{l.name}</td>
                  <td className="px-4 py-3">{l.mobile}</td>
                  <td className="px-4 py-3">{l.email || '—'}</td>
                  <td className="px-4 py-3">{l.configuration || '—'}</td>
                  <td className="px-4 py-3">{l.source}</td>
                  <td className="px-4 py-3">{l.utm_source || '—'}</td>
                  <td className="px-4 py-3">{l.utm_campaign || '—'}</td>
                  <td className="px-4 py-3 max-w-[200px] truncate" title={l.page_url}>{l.page_url}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default Admin;
